import * as Api from '../weborder/Api';
import {
  AddressSuggestion,
  FormInputs,
  GoogleGeocodeResponse,
} from '../weborder/component/CustomLocation';
import * as Data from '../weborder/Data';
import { LoadMaps } from '../weborder/MapLoader';
import { Routes } from '../weborder/Routes';
import { locationUtils } from '.';

const getCoordinates = async (
  googleMapsApiKey: string,
  address: string,
): Promise<Api.Geo | undefined> => {
  try {
    let locationGeo: Api.Geo | undefined;
    const tools = await LoadMaps(googleMapsApiKey);

    await tools.GeoCoder.geocode(
      {
        address,
      },
      (results: google.maps.GeocoderResult[]) => {
        if (results) {
          locationGeo = Data.ToGeo(results[0].geometry.location);
        }
      },
    );

    return locationGeo;
  } catch (error) {
    return undefined;
  }
};

// legacy - remove when Address Internationalisation is 100% released
/* eslint-disable */
const handleGeocodeResponse = (
  results: google.maps.GeocoderResult[],
  status: google.maps.GeocoderStatus,
  appConfig: Data.Async<Api.AppConfig>,
  addressSuggestion: AddressSuggestion,
  params: Routes['params'],
  defaultAddress: FormInputs,
  setDefaultAddress: React.Dispatch<React.SetStateAction<FormInputs>>,
): JSX.Element | null => {
  if (appConfig.status !== 'success') {
    return null;
  }

  const addressComponents = results[0].address_components;

  const isUSAddress = locationUtils.getIsUSAddress(appConfig.data);

  const stateNameValue = isUSAddress
    ? locationUtils.getStateShortName(addressComponents)
    : null;

  const { suggestion } = addressSuggestion;

  if (addressSuggestion.suggestion && !params.lat && !params.lng) {
    // https://trello.com/c/57UyvrHD/1029-new-delivery-location-round-2
    // We don't want to trust the results of the Google Places API call.
    // Sometimes the result of the places API call is not as descriptive as the
    // input.
    // Conor's Address ("Parkfield House, Dublin, Ireland"), for example, will
    // be returned as "Mount Merrion, Ireland".
    // Instead we take the string that the user click on, and split it on comma.
    // We don't just take the first 3 elements though - we combine all address
    // fields after the third into the third.

    const [buildingValue, streetValue, ...remainingAddressDetails] = suggestion
      .split(',')
      .map((s) => s.trim());

    if (remainingAddressDetails.length > 1) {
      remainingAddressDetails.pop();
    }
    const townValue = remainingAddressDetails.join(', ');
    const postcode = appConfig.data.PostCodeInputLabel
      ? addressSuggestion.postalCode
      : '';

    setDefaultAddress({
      building: buildingValue,
      instructions: defaultAddress?.instructions,
      postcode,
      stateName: stateNameValue,
      street: streetValue,
      town: townValue,
    });
  } else {
    if (status !== google.maps.GeocoderStatus.OK) {
      // Raven.captureMessage('Error loading geocode response', {
      //   tags: { category: 'Third party library failure' },
      //   extra: {
      //     status: status,
      //     results: results,
      //   },
      // });
      return;
    }

    // No way it can reach here, it would crash on line 24 'const addressComponents = results[0].address_components;' before reaching here
    if (results.length === 0) {
      return;
    }

    try {
      let a = '';
      let roomNumber = '';
      let streetNumber = '';

      for (const addressComponent of addressComponents) {
        if (addressComponent.types[0] === 'room') {
          roomNumber = addressComponent.long_name;
          break;
        }
      }

      for (const addressComponent of addressComponents) {
        if (addressComponent.types[0] === 'street_number') {
          streetNumber = addressComponent.long_name;
          break;
        }
      }

      const typesOfDataInGeoResponse = [];
      for (const addressComponent of addressComponents) {
        typesOfDataInGeoResponse.push(addressComponent.types[0]);
        if (
          !typesOfDataInGeoResponse.includes('street_number') &&
          appConfig.data.DeliveryAddressLayout === 0
        ) {
          streetNumber = suggestion.split(',')[0];
        }
      }

      if (streetNumber) {
        if (isUSAddress) {
          a = roomNumber;
        } else if (appConfig.data.DeliveryAddressLayout === 2) {
          a = streetNumber;
        } else if (a !== '') {
          a = `${a}, ${streetNumber}`;
        } else {
          a = streetNumber;
        }
      }

      let b = '';
      let route = '';
      let locality = '';

      for (const addressComponent of addressComponents) {
        if (addressComponent.types[0] === 'route') {
          route = addressComponent.long_name;
          break;
        }
      }

      for (const addressComponent of addressComponents) {
        if (
          addressComponent.types[0] === 'neighborhood' ||
          addressComponent.types[0] === 'sublocality'
        ) {
          locality = addressComponent.long_name;
          break;
        }
      }

      b = route;

      if (locality) {
        if (b !== '') {
          b = `${b}, ${locality}`;
        } else {
          b = locality;
        }
      }

      let c = '';
      for (const addressComponent of addressComponents) {
        if (
          addressComponent.types[0] === 'locality' ||
          addressComponent.types[0] === 'postal_town'
        ) {
          c = addressComponent.long_name;
          break;
        }
      }

      let d = '';

      if (appConfig.data.PostCodeInputLabel) {
        for (const addressComponent of addressComponents) {
          if (addressComponent.types[0] === 'postal_code') {
            d = addressComponent.long_name;
          }
        }
      }

      let buildingValue =
        appConfig.data.DeliveryAddressLayout === 2
          ? ''
          : a || defaultAddress?.building;
      let streetValue = isUSAddress
        ? `${streetNumber} ${b}`
        : appConfig.data.DeliveryAddressLayout === 2 && a
        ? `${a} ${b}`
        : b || defaultAddress?.street;
      let townValue = c || defaultAddress?.town;
      const postcodeValue = d || defaultAddress?.postcode;

      if (buildingValue.startsWith('Unnamed ')) {
        buildingValue = '';
      }

      if (streetValue.startsWith('Unnamed ')) {
        streetValue = '';
      }

      if (townValue.startsWith('Unnamed ')) {
        townValue = '';
      }

      setDefaultAddress({
        building: buildingValue,
        instructions: defaultAddress?.instructions,
        postcode: postcodeValue,
        stateName: stateNameValue,
        street: streetValue,
        town: townValue,
      });
    } catch (error) {
      // Raven.captureMessage('Address splitting failed', {
      //   tags: { category: 'Frontend error' },
      //   extra: {
      //     exception: e,
      //     addressComponents: results[0].address_components,
      //   },
      // });
    }
  }
};
/* eslint-enable */

export const initializeMap = async (
  params: Routes['params'],
  googleMapsApiKey: string,
): Promise<GoogleGeocodeResponse> => {
  try {
    const tools = await LoadMaps(googleMapsApiKey);
    const query = {
      ...(params?.placeId
        ? {
            placeId: params.placeId,
          }
        : {
            location: {
              lat: parseFloat(params.lat),
              lng: parseFloat(params.lng),
            },
          }),
    };

    let geocodeResults = [];
    let geocodeStatus = google.maps.GeocoderStatus.ZERO_RESULTS;

    await tools.GeoCoder.geocode(
      query,
      (
        results: google.maps.GeocoderResult[],
        status: google.maps.GeocoderStatus,
      ) => {
        geocodeResults = results;
        geocodeStatus = status;
      },
    );

    return { results: geocodeResults, status: geocodeStatus };
  } catch (error) {
    console.log(error);
    return undefined;
  }
};

const setMap = (locationGeo: Api.Geo): void => {
  const options: google.maps.MapOptions = {
    center: Data.LatLng(locationGeo),
    clickableIcons: false,
    draggable: false,
    mapTypeControl: false, // No map type (terrain/satellite, etc)
    streetViewControl: false, // No street view ControlLabel
    zoom: 17,
    zoomControlOptions: {
      position: google.maps.ControlPosition.RIGHT_CENTER,
    },
  };

  const map = new google.maps.Map(
    document.getElementById('mapRef') as Element,
    options,
  );

  // eslint-disable-next-line no-new
  new google.maps.Marker({
    position: Data.LatLng(locationGeo),
    map,
  });
};

export const mapUtils = {
  getCoordinates,
  handleGeocodeResponse,
  initializeMap,
  setMap,
};
